import { useState, useEffect } from 'react';
import { validateOIB } from '../../utils/validations';
import { t } from '../../i18n/languages';

const AddUserForm = ({
  show,
  currentLanguage,
  newUser,
  setNewUser,
  yearStatus,
  handleCreateUser,
  setShowAddForm,
  loading
}) => {
  const [errors, setErrors] = useState({});
  const [wasShown, setWasShown] = useState(false);
  // Clear errors and form data when form is opened
  useEffect(() => {
    if (show && !wasShown) {
      setErrors({});
      setWasShown(true);
    } else if (!show && wasShown) {
      setWasShown(false); // reset so it works next time
    }
  }, [show, wasShown]);

  const handleOIBChange = (e) => {
    const newOIB = e.target.value;
    setNewUser({ ...newUser, oib: newOIB });

    // Clear previous OIB error when user starts typing
    if (errors.oib) {
      setErrors({ ...errors, oib: '' });
    }

    // Validate OIB in real-time if it has 11 digits
    if (newOIB.length === 11) {
      const validation = validateOIB(newOIB, currentLanguage);
      if (!validation.isValid) {
        setErrors({ ...errors, oib: validation.message });
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate OIB before submission
    const newErrors = {};
    if (newUser.oib) {
      const oibValidation = validateOIB(newUser.oib, currentLanguage);
      if (!oibValidation.isValid) {
        newErrors.oib = oibValidation.message;
      }
    }

    // Add other validations here if needed (email, username, etc.)
    if (!newUser.username?.trim()) {
      newErrors.username = t('userNameRequired', {}, currentLanguage);
    }
    if (!newUser.email?.trim()) {
      newErrors.email = t('emailRequired', {}, currentLanguage);
    }
    if (!newUser.first_name?.trim()) {
      newErrors.first_name = t('firstNameRequired', {}, currentLanguage);
    }
    if (!newUser.last_name?.trim()) {
      newErrors.last_name = t('lastNameRequired', {}, currentLanguage);
    }
    if (!newUser.oib?.trim()) {
      newErrors.oib = t('oibRequired', {}, currentLanguage);
    }
    if (newUser.vacation_contract <= 0) {
      newErrors.vacation_contract = t('vacationContractRequired', {}, currentLanguage);
    }
    if (newUser.employment_start_date === '') {
      newErrors.employment_start_date = t('employmentStartDateRequired', {}, currentLanguage);
    }

    setErrors(newErrors);

    // If there are validation errors, don't submit
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    handleCreateUser(e);
  };

  if (!show) return null;

  return (
    <div style={formContainer}>
      <h3 style={formTitle}>
        {t('addNewUser', {}, currentLanguage)}
      </h3>

      <form onSubmit={handleSubmit}>
        <div style={twoColGrid}>
          <Input
            label={t('username', {}, currentLanguage)}
            value={newUser.username}
            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
            placeholder="e.g. jdoe"
            error={errors.username}
          />

          <Input
            type="email"
            label="Email"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            placeholder="john.doe@7pay.hr"
            error={errors.email}
          />
        </div>

        <div style={fourColGrid}>
          <Input
            label={t('firstName', {}, currentLanguage)}
            value={newUser.first_name}
            onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
            placeholder="John"
            error={errors.first_name}
          />

          <Input
            label={t('lastName', {}, currentLanguage)}
            value={newUser.last_name}
            onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
            placeholder="Doe"
            error={errors.last_name}
          />

          <div>
            <Label text={t('role', {}, currentLanguage)} />
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              style={inputStyle}
            >
              <option value="employee">{t('employee', {}, currentLanguage)}</option>
              <option value="admin">{t('admin', {}, currentLanguage)}</option>
            </select>
          </div>

          <Input
            type="number"
            label={t('vacationDays', {}, currentLanguage)}
            value={newUser.vacation_contract}
            onChange={(e) => setNewUser({ ...newUser, vacation_contract: parseInt(e.target.value) || 0 })}
            min="0"
            error={errors.vacation_contract}
          />
        </div>

        <div style={twoColGrid}>
          <Input
            type="number"
            label={
              currentLanguage === 'hr'
                ? `G.O. za tekuću godinu (${yearStatus?.currentOpenYear || new Date().getFullYear()})`
                : `Vacation Days for Current Year (${yearStatus?.currentOpenYear || new Date().getFullYear()})`
            }
            value={newUser.current_year_contract}
            onChange={(e) => setNewUser({ ...newUser, current_year_contract: e.target.value })}
            min="0"
            placeholder={t('calculatedVacation', {}, currentLanguage)}
          />

          <Input
            type="date"
            label={t('employmentStartDate', {}, currentLanguage)}
            value={newUser.employment_start_date}
            onChange={(e) => setNewUser({ ...newUser, employment_start_date: e.target.value })}
            error={errors.employment_start_date}
          />
        </div>

        <div style={twoColGrid}>
          <div>
            <Label text={t('employmentType', {}, currentLanguage)} />
            <select
              value={newUser.employment_type}
              onChange={(e) => setNewUser({ ...newUser, employment_type: e.target.value })}
              style={inputStyle}
            >
              <option value="full-time">{t('fullTime', {}, currentLanguage)}</option>
              <option value="part-time">{t('partTime', {}, currentLanguage)}</option>
            </select>
          </div>

          <Input
            type="number"
            label={currentLanguage === 'hr' ? 'OIB' : 'OIB'}
            value={newUser.oib || ''}
            onChange={handleOIBChange}
            error={errors.oib}
            min="0"
          />
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '12px 24px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading
              ? currentLanguage === 'hr' ? 'Stvaranje...' : 'Creating...'
              : t('addUser', {}, currentLanguage)}
          </button>

          <button
            type="button"
            onClick={() => setShowAddForm(false)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            {t('cancel', {}, currentLanguage)}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddUserForm;

const Input = ({ label, type = 'text', error, ...props }) => (
  <div>
    <Label text={label} />
    <input type={type} style={inputStyle} {...props} />
    {error && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{error}</div>}
  </div>
);

const Label = ({ text }) => (
  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
    {text}
  </label>
);

// Styles

const formContainer = {
  backgroundColor: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  padding: '24px',
  marginBottom: '24px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
};

const formTitle = {
  fontSize: '18px',
  fontWeight: '600',
  color: '#1f2937',
  marginBottom: '16px'
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '14px'
};

const twoColGrid = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
  marginBottom: '16px'
};

const fourColGrid = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr 1fr',
  gap: '16px',
  marginBottom: '16px'
};
